/* Theme: reactive light/dark selection.

   The boot-script in index.html sets data-theme="light"|"dark" on <html>
   before first paint (from localStorage if present, otherwise from
   prefers-color-scheme). This module exposes that state as a signal so
   the toggle can read/write it, persists explicit user choices, and
   tracks system-pref changes when the user has not overridden. */

import { signal } from "@preact/signals";

export type Theme = "light" | "dark";

const STORAGE_KEY = "palindragon-theme";

/** Read the live attribute the boot-script wrote. Defaults to "light"
   only as a paranoia fallback — the boot-script always sets one. */
function readDocTheme(): Theme {
  const v = document.documentElement.dataset.theme;
  return v === "dark" ? "dark" : "light";
}

export const theme = signal<Theme>(readDocTheme());

function applyTheme(next: Theme): void {
  document.documentElement.dataset.theme = next;
  theme.value = next;
}

/** User explicitly chose a theme. Persist it so subsequent loads (and
   subsequent system-pref changes) do not override the choice. */
export function setTheme(next: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* private mode / disabled storage — fall through, in-memory only */
  }
  applyTheme(next);
}

export function toggleTheme(): void {
  setTheme(theme.value === "dark" ? "light" : "dark");
}

/** Follow system pref while the user has not made an explicit choice.
   Once they toggle, the localStorage entry is set and this listener
   stops applying changes. Idempotent — safe to call once at startup. */
export function watchSystemTheme(): void {
  const mql = window.matchMedia("(prefers-color-scheme: dark)");
  const onChange = () => {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      /* same as above */
    }
    if (stored === "light" || stored === "dark") return;
    applyTheme(mql.matches ? "dark" : "light");
  };
  mql.addEventListener("change", onChange);
}
